import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import type { GraphModification, TemplateDataspace, LanguageCode } from "@lr-notitia/notitia-designer/schemas";
import { handleGraphModification } from "./graph/utils";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { useSymbolsStore } from "./symbols";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";
import { isTemplateDataspace } from "@lr-notitia/notitia-model-store";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
}

export const useTemplateDataspacesStore = defineStore("TemplateDataspaces", () => {
	init();
	let domainId = "";
	const dataspaces = ref<TemplateDataspace[]>([]);

	function loadDataspaces(dId: string): Promise<void> {
		domainId = dId;
		return new Promise((resolve) => {
			dataspaces.value = Object.values(graphStore.graph.nodes).filter((n) =>
				isTemplateDataspace(n)
			) as TemplateDataspace[];

			resolve();

			api()
				.socket()
				.domain(domainId)
				.template()
				.dataspaces()
				.on((message) => {
					switch (message.method) {
						case "create":
							if (!dataspaces.value.find((d) => d.id === message.data.id))
								dataspaces.value.push(message.data);
							break;
						case "update":
							dataspaces.value = dataspaces.value.map((dataspace) => {
								if (dataspace.id === message.data.id) return message.data;
								return dataspace;
							});
							break;
						case "delete":
							dataspaces.value = dataspaces.value.filter((dataspace) => dataspace.id !== message.data);
							break;
					}
				});
		});
	}
	function clearDataspaces() {
		api().socket().domain(domainId).template().dataspaces().off();
		dataspaces.value = [];
		domainId = "";
	}
	function getById(id: string): TemplateDataspace | undefined {
		return dataspaces.value.find((dataspace) => dataspace.id === id);
	}
	function addDataspace(
		name: string,
		description: string = "",
		language: LanguageCode = "en"
	): Promise<GraphModification> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.dataspaces()
				.post(name, description, language)
				.then((data) => {
					handleGraphModification(data);
					resolve(data);
				})
				.catch(() => reject());
		});
	}
	function updateDataspace(dataspace: TemplateDataspace): Promise<TemplateDataspace> {
		if (!graphStore || domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.template()
				.dataspace(dataspace.id)
				.put(dataspace)
				.then((data) => {
					handleGraphModification(data);
					resolve(data[dataspace.id] as TemplateDataspace);
				})
				.catch(() => reject());
		});
	}
	function removeDataspace(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			let dataspace = getById(id);
			if (!dataspace) return reject();
			api()
				.domain(domainId)
				.template()
				.dataspace(id)
				.del()
				.then((data) => {
					handleGraphModification(data);
					resolve();
				})
				.catch(() => reject());
		});
	}

	const openedDataspaceId = ref<string | undefined>(undefined);
	const openedDataspace = computed(() => {
		if (!openedDataspaceId.value) return undefined;
		return getById(openedDataspaceId.value);
	});

	watch(
		() => dataspaces.value,
		() => {
			if (!graphStore) init();
			dataspaces.value.sort((a, b) => {
				if (
					symbolsStore.term({ id: a.refersTo, terminology: a.terminology || {} }) <
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} })
				)
					return -1;
				if (
					symbolsStore.term({ id: a.refersTo, terminology: a.terminology || {} }) >
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} })
				)
					return 1;
				return 0;
			});
		}
	);
	watch(
		() => openedDataspaceId.value,
		(newId, oldId) => {
			if (oldId) {
				api().socket().domain(domainId).template().dataspace(oldId).off();
			}
			if (newId) {
				api()
					.socket()
					.domain(domainId)
					.template()
					.dataspace(newId)
					.on((message) => {
						const pd = dataspaces.value?.find((dataspace) => dataspace.id === message.id);
						if (pd) {
							pd.refersTo = message.refersTo;
							pd.reviewRequired = message.reviewRequired;
							pd.workspaces = message.workspaces;
						}
					});
			}
		}
	);

	return {
		dataspaces,
		loadDataspaces,
		clearDataspaces,
		getById,
		addDataspace,
		updateDataspace,
		removeDataspace,
		openedDataspaceId,
		openedDataspace,
	};
});
